import { ApiErrorCode, type ApiErrorDto } from '@weloze/shared';

/**
 * Cliente HTTP da API.
 *
 * A sessão viaja em cookie httpOnly, então não há token para guardar nem
 * cabeçalho `Authorization` para montar — só `credentials: 'include'`. Como
 * front e API saem do mesmo domínio, os caminhos são relativos.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** A sessão acabou (ou nunca existiu) e o usuário precisa entrar de novo. */
  get isUnauthenticated(): boolean {
    return (
      this.status === 401 ||
      this.code === ApiErrorCode.UNAUTHENTICATED ||
      this.code === ApiErrorCode.ACCOUNT_INACTIVE
    );
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    // Rede fora, servidor caído, DNS: nada disso tem código HTTP.
    throw new ApiError(
      0,
      ApiErrorCode.INTERNAL_ERROR,
      'Não foi possível falar com o servidor. Verifique sua conexão.',
    );
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload: unknown = isJson ? await response.json() : null;

  if (!response.ok) {
    const error = (payload ?? {}) as Partial<ApiErrorDto>;
    throw new ApiError(
      response.status,
      error.code ?? ApiErrorCode.INTERNAL_ERROR,
      error.message ?? 'Erro inesperado. Tente novamente.',
      error.fields,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  /**
   * Envia um arquivo.
   *
   * O arquivo vai **sozinho no corpo**, e o nome num cabeçalho — não é
   * `multipart`. O servidor grava do socket direto para o disco, sem juntar os
   * bytes na memória, e é por isso que aqui também não se monta `FormData`.
   *
   * `encodeURIComponent` no nome porque cabeçalho HTTP é ASCII, e
   * "Certificação ISO 9001.pdf" não é.
   */
  upload: <T>(path: string, file: File) =>
    request<T>(path, {
      method: 'POST',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'X-File-Name': encodeURIComponent(file.name),
      },
    }),
};
